import React, { useMemo } from "react";

import { getTextColor } from "@intelligems/ig-utils";
import { styled } from "@mui/material";

import { cls } from "../../util";
import { useQuantityButtonsContext } from "../context/QuantityButtonsContext";

interface ExtraMessageProps {
  mode: "hover" | "extra";
  quantity: number;
  unitDiscount: number;
  message?: string;
  fullPriceMessage?: string | null;
  className?: string;
}

export const ExtraMessage = ({
  mode,
  message,
  quantity,
  unitDiscount,
  fullPriceMessage,
  className,
}: ExtraMessageProps) => {
  const { config, widget, formatter } = useQuantityButtonsContext();

  const replacements = widget.variables.textReplacements;
  const save = replacements?.save ?? "Save";
  const off = replacements?.off ?? "Off";
  const percent = replacements?.percent ?? "%";

  const hoverMessage = useMemo(() => {
    let totalDiscount: number;
    if (config.discountType === "dollar") {
      totalDiscount = quantity * unitDiscount;
    } else {
      totalDiscount = unitDiscount;
    }

    if (totalDiscount === 0) {
      return fullPriceMessage ?? "Full Price";
    } else {
      return config.discountType === "dollar"
        ? `${save} ${formatter(quantity * unitDiscount)}`
        : `${unitDiscount.toFixed(0)}${percent} ${off}`;
    }
  }, [quantity, unitDiscount, config.discountType]);

  if (mode === "hover" && message && message !== "") {
    return (
      <Hover className={cls("ig-volume-widget-extra-message-hover", className)}>
        {hoverMessage}
      </Hover>
    );
  }
  if (!message || message === "") {
    return (
      <Base className={cls("ig-volume-widget-extra-message-empty", className)}>
        &nbsp;
      </Base>
    );
  }
  return (
    <Container className={cls("ig-volume-widget-extra-message", className)}>
      {message}
    </Container>
  );
};

const Base = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const { device } = widget;
  return {
    textAlign: "center",
    margin: "-13px 20px 0 20px",
    borderRadius: 9,
    width: "unset",
    minWidth: 100,
    height: device === "desktop" ? 24 : 18,
    fontSize: device === "desktop" ? 13 : 12,
    lineHeight: `${device === "desktop" ? 13 : 12}px`,
    padding: device === "desktop" ? "5px 0 0 0" : "3px 0 3px 0",
  };
});

const Container = styled(Base)(() => {
  const { widget } = useQuantityButtonsContext();
  const { primary } = widget.styles.colors;
  return {
    backgroundColor: primary,
    color: getTextColor(primary),
  };
});

const Hover = styled(Base)(() => {
  const { widget } = useQuantityButtonsContext();
  const { secondary } = widget.styles.colors;
  return {
    backgroundColor: secondary,
    color: getTextColor(secondary),
  };
});
